import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowRight, Check, CheckCircle2, Upload } from 'lucide-react';
import { client } from '@/lib/api';
import { useSetupStatus } from '@/lib/setup-status';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { CALLBACK_MESSAGES } from '@/lib/gmail-callback';
import SignatureEditor from '@/components/SignatureEditor';
import ImportDialog from './vendors/ImportDialog';

/**
 * First-time setup — the 2026-09-25 design.
 *
 * ## Why this is a checklist and not a wizard
 *
 * Every step is visible and actionable at once. A planner who opens this to
 * fix one thing can fix that one thing and leave; nothing is gated behind a
 * Next button. The steps are also not numbered, deliberately: numbers promise
 * an order, and only one dependency actually exists here — importing a
 * signature needs a mailbox to read it from, which the signature step says in
 * its own words when the mailbox is missing.
 *
 * The rolodex sits below a rule and outside the count, because it is the one
 * step a planner can be finished without. Counting it would leave someone who
 * keeps their vendors in their head permanently at "3 of 4", and a checklist
 * that cannot be completed is a checklist people stop reading.
 *
 * ## Leaving is always allowed
 *
 * "Go to dashboard" is never disabled. `SetupCard` follows up on the
 * dashboard until the three required steps are done, which is a better
 * instrument than trapping someone here.
 */

/**
 * One row of the checklist.
 *
 * The marker is the only thing that reads state, and it reads it in two
 * channels rather than one: a filled disc with a check, and the step's own
 * text going quiet. Color alone would leave the state invisible to anyone who
 * cannot separate sage from stone.
 */
function Step({
  title,
  hint,
  done,
  optional,
  children,
}: {
  title: string;
  /** Only where the step is not self-evident from its own controls. */
  hint?: string;
  done: boolean;
  optional?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex gap-4 py-6 first:pt-0 last:pb-0">
      <span
        aria-hidden
        className={[
          'mt-0.5 grid size-6 shrink-0 place-items-center rounded-full border transition-colors duration-300',
          done
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border bg-[var(--color-surface-container-high)]',
        ].join(' ')}
      >
        {done && <Check className="size-3.5" strokeWidth={3} />}
      </span>

      <div className="min-w-0 flex-1 space-y-3">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold">
            {title}
            {optional && (
              <span className="ml-2 text-xs font-normal text-muted-foreground">(optional)</span>
            )}
            <span className="sr-only">{done ? ' — done' : ' — not done yet'}</span>
          </h3>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
        {children}
      </div>
    </div>
  );
}

export default function WelcomePage() {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const { data: status } = useSetupStatus();

  const [details, setDetails] = useState({ name: '', businessName: '' });
  const [touched, setTouched] = useState({ name: false, businessName: false });
  const [signatureHtml, setSignatureHtml] = useState('');
  const [signatureTouched, setSignatureTouched] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);

  /**
   * The signature's own HTML, which `setup/status` deliberately does not
   * carry — it reports whether one exists, not what it says.
   *
   * Loaded here because the editor has to open holding the real signature. A
   * planner who already has one and lands on /welcome would otherwise see an
   * empty editor over a signature that exists, and one Save would wipe it.
   */
  const { data: settings } = useQuery({
    queryKey: ['user-settings'],
    queryFn: async () => {
      const res = await client.api['user-settings'].me.$get();
      if (!res.ok) throw new Error('Failed to load your settings');
      return res.json();
    },
  });

  useEffect(() => {
    if (!settings || signatureTouched) return;
    setSignatureHtml(settings.emailSignatureHtml ?? '');
  }, [settings, signatureTouched]);

  /**
   * Seeded from the server, per field.
   *
   * Per field rather than per form because these inputs are live before the
   * status query resolves. A planner who starts typing their business name in
   * that window would, under a single form-wide dirty flag, block the seed for
   * the *name* field too — and then save a blank name over a good one, or find
   * the save button inexplicably disabled. Each field stops accepting the
   * server's value only once it has been edited itself.
   */
  useEffect(() => {
    if (!status) return;
    setDetails((current) => ({
      name: touched.name ? current.name : (status.name ?? ''),
      businessName: touched.businessName ? current.businessName : (status.businessName ?? ''),
    }));
  }, [status, touched]);

  /** Set once, on the hop back from a consent screen that actually connected. */
  const justConnected = useRef(false);

  // Surface the OAuth result once, then strip it from the URL so a reload does
  // not re-announce a connection that happened ten minutes ago.
  useEffect(() => {
    const code = params.get('gmail');
    if (!code) return;
    if (code === 'connected') justConnected.current = true;
    const message = CALLBACK_MESSAGES[code];
    if (message) (message.ok ? toast.success : toast.error)(message.text);
    params.delete('gmail');
    setParams(params, { replace: true });
  }, [params, setParams]);

  /**
   * Fetch the planner's signature out of their sent mail the moment a mailbox
   * is attached, and save what comes back.
   *
   * The manual version of this is one button with no input, and the planner
   * has just spent a consent screen proving they want their mail connected —
   * so the click is taken for them. It saves rather than merely filling the
   * editor because the import writes the signature's images into storage
   * either way: importing without saving would spend those writes and leave
   * the planner with nothing to show for them.
   *
   * Saving is safe to do unreviewed because the extraction is not a guess.
   * It keys on Gmail's own `gmail_signature` marker and refuses rather than
   * guesses when the only match sits inside a quoted reply. The result is on
   * screen in the editor and the preview immediately, with a note saying where
   * it came from, and nothing reaches a vendor without passing the compose
   * preview first.
   *
   * Three conditions, all necessary: only on the hop back from Google (a scan
   * of ten sent messages on every mount is a scan nobody asked for), only with
   * a grant that can read mail, and only when there is no signature to
   * overwrite.
   */
  const autoImported = useRef(false);
  useEffect(() => {
    if (!justConnected.current || autoImported.current) return;
    if (!status?.hasReadAccess || status.hasSignature) return;

    autoImported.current = true;
    void (async () => {
      try {
        const res = await client.api.signature['import-from-mail'].$post();
        if (!res.ok) return;
        const result = await res.json();
        if (!result || !('found' in result) || !result.found) return;

        const put = await client.api['user-settings'].me.$put({
          json: { emailSignatureHtml: result.html },
        });
        if (!put.ok) return;

        setSignatureHtml(result.html);
        void queryClient.invalidateQueries({ queryKey: ['setup-status'] });
        void queryClient.invalidateQueries({ queryKey: ['user-settings'] });
        toast.success('We found your signature in a recent email and saved it.');
      } catch {
        // Nobody asked for this, so nobody is told it did not work. The two
        // choices they would have had anyway are still sitting there.
      }
    })();
  }, [status, queryClient]);

  const connect = useMutation({
    mutationFn: async () => {
      // `returnTo` is what brings the browser back here rather than to
      // Settings — see the `return_to` column on `oauth_states`.
      const res = await client.api.gmail.connect.$post({ json: { returnTo: 'welcome' } });
      if (!res.ok) throw new Error('Could not start the Google connection');
      return res.json();
    },
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: () => toast.error('Could not reach Google. Try again.'),
  });

  const saveDetails = useMutation({
    mutationFn: async () => {
      const res = await client.api['user-settings'].me.$put({
        json: { name: details.name.trim(), businessName: details.businessName.trim() },
      });
      if (!res.ok) throw new Error('Could not save your details');
      return res.json();
    },
    onSuccess: (saved) => {
      toast.success('Saved.');
      setEditingDetails(false);

      /**
       * The saved row goes into the cache directly, rather than the step
       * waiting on a refetch to find out what it just did.
       *
       * This is what the earlier version got wrong. It cleared the form's
       * dirty flags on success, which fired the seeding effect against the
       * `setup/status` still sitting in the cache from before the save — and
       * wiped the field the planner had just saved. A slow refetch made that
       * a flash; a refetch that never landed made it permanent, leaving an
       * empty box and an unfilled marker over a value already in the database.
       */
      queryClient.setQueryData(['setup-status'], (old: unknown) =>
        old && typeof old === 'object'
          ? { ...old, name: saved?.name ?? null, businessName: saved?.businessName ?? null }
          : old,
      );
      void queryClient.invalidateQueries({ queryKey: ['setup-status'] });
    },
    onError: () => toast.error('Could not save your details. Try again.'),
  });

  const saveSignature = useMutation({
    mutationFn: async () => {
      const res = await client.api['user-settings'].me.$put({
        json: { emailSignatureHtml: signatureHtml },
      });
      if (!res.ok) throw new Error('Could not save your signature');
      return res.json();
    },
    onSuccess: () => {
      toast.success('Signature saved.');
      setSignatureTouched(false);
      void queryClient.invalidateQueries({ queryKey: ['setup-status'] });
      void queryClient.invalidateQueries({ queryKey: ['user-settings'] });
    },
    onError: () => toast.error('Could not save your signature. Try again.'),
  });

  const detailsValid = details.name.trim().length > 0 && details.businessName.trim().length > 0;
  // Settled once the server holds a business name and the planner is not
  // currently editing it back.
  const detailsSettled = Boolean(status?.businessName) && !editingDetails;
  // An empty editor is not a signature. Saving one would clear the text twin
  // as well and read as success, so the button simply does not offer it.
  const signatureValid = signatureHtml.replace(/<[^>]*>/g, '').trim().length > 0;
  const vendorCount = status?.vendorCount ?? 0;

  // Only the steps a planner cannot send without. The signature and the
  // rolodex are offered on the same list but never counted, so the count is a
  // number that can actually be finished.
  const done = [status?.gmailConnected, Boolean(status?.businessName)].filter(Boolean).length;

  return (
    <div className="mx-auto max-w-2xl space-y-8">
      <h1 className="display-tight text-3xl font-semibold">Let's get set up.</h1>

      <Card className="rounded-3xl bg-card shadow-ambient">
        <CardContent className="space-y-0 divide-y divide-border pt-6">
          <div className="flex items-center justify-between gap-4 pb-4">
            <h2 className="label-caps text-xs text-muted-foreground">Setup</h2>
            <p className="label-caps text-xs text-muted-foreground">{done} of 2 done</p>
          </div>

          <Step
            title="Connect Gmail"
            done={Boolean(status?.gmailConnected)}
          >
            {status?.gmailConnected ? (
              <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-[var(--color-surface-container-high)] px-4 py-3">
                <CheckCircle2 className="size-5 text-primary" />
                <GmailIdentity />
              </div>
            ) : (
              <div className="space-y-3">
                <Button disabled={connect.isPending} onClick={() => connect.mutate()}>
                  {connect.isPending ? 'Opening Google…' : 'Connect Gmail'}
                </Button>
                <p className="text-xs text-muted-foreground">
                  VendorOps reads, labels, and sends. It never deletes.
                </p>
              </div>
            )}
          </Step>

          <Step
            title="Your name and business"
            done={Boolean(status?.businessName)}
          >
            {detailsSettled ? (
              /* Settled: what the planner saved, as text. A form still sitting
                 there with a live Save button reads as work outstanding. */
              <div className="flex flex-wrap items-end justify-between gap-4">
                <dl className="grid gap-4 sm:grid-cols-2 sm:gap-8">
                  <div className="space-y-1">
                    <dt className="label-caps text-xs text-muted-foreground">Your name</dt>
                    <dd className="text-sm font-medium">{status?.name}</dd>
                  </div>
                  <div className="space-y-1">
                    <dt className="label-caps text-xs text-muted-foreground">Business name</dt>
                    <dd className="text-sm font-medium">{status?.businessName}</dd>
                  </div>
                </dl>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setTouched({ name: false, businessName: false });
                    setEditingDetails(true);
                  }}
                >
                  Edit
                </Button>
              </div>
            ) : (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="welcome-name">Your name</Label>
                    <Input
                      id="welcome-name"
                      value={details.name}
                      placeholder="Amy Chen"
                      onChange={(e) => {
                        setDetails((d) => ({ ...d, name: e.target.value }));
                        setTouched((t) => ({ ...t, name: true }));
                      }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="welcome-business">Business name</Label>
                    <Input
                      id="welcome-business"
                      value={details.businessName}
                      placeholder="Wildwood Events"
                      onChange={(e) => {
                        setDetails((d) => ({ ...d, businessName: e.target.value }));
                        setTouched((t) => ({ ...t, businessName: true }));
                      }}
                    />
                  </div>
                </div>

                <Button
                  disabled={!detailsValid || saveDetails.isPending}
                  onClick={() => saveDetails.mutate()}
                >
                  {saveDetails.isPending ? 'Saving…' : 'Save'}
                </Button>
              </>
            )}
          </Step>

          <Step
            title="Email signature"
            optional
            done={Boolean(status?.hasSignature)}
          >
            <SignatureEditor
              value={signatureHtml}
              onChange={(html) => {
                setSignatureHtml(html);
                setSignatureTouched(true);
              }}
            />
            {/* Absent rather than disabled while there is nothing to save.
                The editor opens on a chooser — import, or build by hand — and
                a dead Save button under it is a control with no job yet. */}
            {signatureValid && (
              <Button disabled={saveSignature.isPending} onClick={() => saveSignature.mutate()}>
                {saveSignature.isPending ? 'Saving…' : 'Save signature'}
              </Button>
            )}
          </Step>
          <Step
            title="Import your rolodex"
            optional
            hint="Upload the spreadsheet you already keep — .csv or .xlsx. We read it and show you what we found before anything is saved."
            done={vendorCount > 0}
          >
            <Button variant="outline" onClick={() => setImportOpen(true)}>
              <Upload className="size-4" />
              Upload a spreadsheet
            </Button>
            {vendorCount > 0 && (
              <p className="text-xs text-muted-foreground">
                {vendorCount} vendors in your rolodex.
              </p>
            )}
            <ImportDialog open={importOpen} onOpenChange={setImportOpen} />
          </Step>
        </CardContent>
      </Card>

      {/* Held shut until both required steps land.
          The dashboard renders reply state, stale inquiries and a review
          queue, every one of which needs a connected mailbox — so before
          Gmail is attached this link leads to an empty page carrying a card
          that says to come back here. Saying "not yet, and why" is better
          than sending a planner to a dead end and letting them find out.
          It is not a trap: the nav above still goes anywhere. */}
      <div className="flex flex-wrap items-center justify-end gap-4">
        {!status?.complete && (
          <p className="text-xs text-muted-foreground">
            {status?.gmailConnected
              ? 'Add your name and business to open your dashboard.'
              : 'Connect Gmail to open your dashboard.'}
          </p>
        )}

        {status?.complete ? (
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            Go to dashboard
            <ArrowRight className="size-4" />
          </Link>
        ) : (
          <span
            aria-disabled
            className="inline-flex cursor-not-allowed items-center gap-2 text-sm font-medium text-muted-foreground opacity-60"
          >
            Go to dashboard
            <ArrowRight className="size-4" />
          </span>
        )}
      </div>
    </div>
  );
}

/** The connected mailbox, read from the Gmail status the rest of the app uses. */
function GmailIdentity() {
  const { data } = useQuery({
    queryKey: ['gmail-status'],
    queryFn: async () => {
      const res = await client.api.gmail.status.$get();
      if (!res.ok) throw new Error('Failed to fetch Gmail status');
      return res.json();
    },
  });

  if (!data?.connected) return null;
  return (
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-medium">{data.email}</p>
      {!data.hasReadAccess && (
        <p className="text-xs text-muted-foreground">Send-only — replies won't be tracked.</p>
      )}
    </div>
  );
}
